import { gql, useQuery, WatchQueryFetchPolicy } from '@apollo/client';
import { EMPTY_ARRAY } from '@modules/common/constants';
import {
  useIsMetadataNonGroupabilityEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import { useFetchListMetadataKeys } from '@modules/metadataManagement/hooks/useFetchListMetadataKeys';
import {
  EqlField,
  EqlFieldValueType,
  ListEqlFieldsInput,
  Query,
} from 'generated/graphql';
import { useMemo } from 'react';
import { UUID } from 'v2/core/entities/uuid';
import { useGetUser } from 'v2/core/services/user';
import { FEEDBACK_RECORDS_EQL_FIELD } from '../components/EqlFieldsAutoComplete/constants';
import { getGroupLabelFromEqlField } from '../components/EqlFieldsAutoComplete/helpers';

export const LIST_EQL_FIELDS_GQL = gql`
  query listEQLFields($tenantID: ID!, $groupID: String, $searchTerm: String) {
    listEQLFields(
      request: {
        tenantID: $tenantID
        groupID: $groupID
        searchTerm: $searchTerm
      }
    ) {
      fields {
        field
        displayName
        iconURL
        objectType
        valueType
        isMultiValue
        detail {
          metadata {
            source
            references {
              key
              displayName
              source
              iconURL
            }
          }
          reference {
            creatorName
            referenceID
            referenceType
            creatorID
          }
        }
      }
    }
  }
`;

interface useFetchEQLFieldsParams {
  groupID?: UUID;
  fetchPolicy?: WatchQueryFetchPolicy;
  shouldExcludeNonGroupByFields?: boolean;
  shouldExcludeNonGroupableMetadataFields?: boolean;
  shouldIncludeFeedbackRecordsFilter?: boolean;
}

export function useFetchEQLFields({
  groupID,
  fetchPolicy = 'cache-first',
  shouldExcludeNonGroupByFields = false,
  shouldExcludeNonGroupableMetadataFields = false,
  shouldIncludeFeedbackRecordsFilter = false,
}: useFetchEQLFieldsParams = {}) {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const isMetadataNonGroupabilityEnabled =
    useIsMetadataNonGroupabilityEnabled();

  const resp = useQuery<Query, ListEqlFieldsInput>(LIST_EQL_FIELDS_GQL, {
    variables: {
      tenantID,
      searchTerm: undefined,
      groupID,
    },
    fetchPolicy,
    nextFetchPolicy: 'cache-first',
  });

  const { metadataKeys, isLoading: isLoadingListMetadataKeys } =
    useFetchListMetadataKeys({
      shouldSkip:
        !shouldExcludeNonGroupableMetadataFields ||
        isMetadataNonGroupabilityEnabled,
    });

  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();

  const filteredEQLFields = useMemo(() => {
    if (isLoadingListMetadataKeys) {
      return null;
    }

    const eqlFieldsResp = resp.data?.listEQLFields?.fields;

    if (!eqlFieldsResp) {
      return null;
    }

    let eqlFields: EqlField[] = [...eqlFieldsResp];

    if (shouldIncludeFeedbackRecordsFilter) {
      // find the last member in the eqlFields that belong to Enterpret Array and push it after that since order matter
      let lastIndexOfEnterpretGroup = -1;

      for (let index = eqlFields.length - 1; index >= 0; index--) {
        const eqlField = eqlFields[index];
        if (
          getGroupLabelFromEqlField({
            eqlField,
            shouldRemoveFileUploadPrefix,
          }) === 'Enterpret'
        ) {
          lastIndexOfEnterpretGroup = index;
          break;
        }
      }

      eqlFields.splice(
        lastIndexOfEnterpretGroup + 1,
        0,
        FEEDBACK_RECORDS_EQL_FIELD,
      );
    }

    // TODO: Remove when aggregate pagination is implemented
    const nonGroupableMetadataFields = shouldExcludeNonGroupableMetadataFields
      ? new Set(
          metadataKeys
            ?.filter((metadataKey) => metadataKey.isGroupable === false)
            .map((metadataKey) => metadataKey.key) ?? [],
        )
      : null;

    const filteredEQLFields = eqlFields?.filter((eqlField) => {
      if (eqlField.field === 'entitySentiment') {
        return false;
      }

      if (eqlField.field === 'search' && shouldExcludeNonGroupByFields) {
        return false;
      }

      if (eqlField.field === 'category' && shouldExcludeNonGroupByFields) {
        return false;
      }

      if (eqlField.field === 'summary' && shouldExcludeNonGroupByFields) {
        return false;
      }

      if (
        eqlField.valueType === EqlFieldValueType.None &&
        shouldExcludeNonGroupByFields
      ) {
        return false;
      }

      if (
        !!nonGroupableMetadataFields?.size &&
        nonGroupableMetadataFields.has(eqlField.field)
      ) {
        return false;
      }

      return true;
    });

    return filteredEQLFields;
  }, [
    isLoadingListMetadataKeys,
    resp.data?.listEQLFields?.fields,
    shouldExcludeNonGroupableMetadataFields,
    metadataKeys,
    shouldExcludeNonGroupByFields,
    shouldIncludeFeedbackRecordsFilter,
    shouldRemoveFileUploadPrefix,
  ]);

  return {
    fields: filteredEQLFields ?? EMPTY_ARRAY,
    isLoading: filteredEQLFields
      ? false
      : resp.loading || isLoadingListMetadataKeys,
  };
}
